import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FaDownload, FaPaperclip, FaSearchMinus, FaSearchPlus, FaTrash, FaUpload } from "react-icons/fa";
import axiosInstance from "../components/api/axiosInstance";
import { resolveImageUrl } from "../config/apiConfig";
import { prepareAttachmentFile } from "../utils/prepareAttachmentFile";
import PortalAlert from "./PortalAlert";
import PortalConfirmModal from "./PortalConfirmModal";
import { canDeleteInPortal } from "./portalSession";

function fileUrl(attachment) {
  const raw = attachment?.File_Url || attachment?.File_Path;
  if (!raw) return null;
  return resolveImageUrl(raw);
}

function isImageAttachment(attachment) {
  const name = String(attachment?.File_Name || attachment?.File_Path || "");
  return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name);
}

/**
 * Per-entity attachments manager with left file list + large right preview.
 */
function PortalEntityAttachmentsModal({
  open,
  onClose,
  tenantClientId,
  entityType,
  entityId,
  entityName,
}) {
  const fileInputRef = useRef(null);
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [remarks, setRemarks] = useState("");
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [chosenFileName, setChosenFileName] = useState("");
  const [zoom, setZoom] = useState(1);
  const canDelete = useMemo(() => canDeleteInPortal(), []);

  const entitySegment = entityType === "project" ? "projects" : "services";
  const basePath = `/client/tenant-clients/${tenantClientId}/${entitySegment}/${entityId}/attachments`;

  const loadAttachments = useCallback(async () => {
    if (!tenantClientId || !entityId) return;
    setLoading(true);
    setError("");
    try {
      const res = await axiosInstance.get(basePath);
      const rows = Array.isArray(res.data?.data) ? res.data.data : [];
      setAttachments(rows);
      setSelectedId((prev) => {
        if (prev && rows.some((row) => row.Tenant_Client_Attachment_Id === prev)) return prev;
        return rows[0]?.Tenant_Client_Attachment_Id ?? null;
      });
    } catch (err) {
      setAttachments([]);
      setSelectedId(null);
      setError(err.response?.data?.message || "Could not load attachments.");
    } finally {
      setLoading(false);
    }
  }, [basePath, entityId, tenantClientId]);

  useEffect(() => {
    if (!open) return undefined;
    loadAttachments();
    setRemarks("");
    setChosenFileName("");
    setZoom(1);
    if (fileInputRef.current) fileInputRef.current.value = "";
    return undefined;
  }, [open, loadAttachments]);

  useEffect(() => {
    setZoom(1);
  }, [selectedId]);

  const selected = useMemo(
    () => attachments.find((row) => row.Tenant_Client_Attachment_Id === selectedId) || null,
    [attachments, selectedId]
  );
  const selectedUrl = selected ? fileUrl(selected) : null;
  const selectedIsImage = selected && selectedUrl && isImageAttachment(selected);

  const handleUpload = async (e) => {
    e.preventDefault();
    const file = fileInputRef.current?.files?.[0];
    if (!file) {
      setError("File is required.");
      return;
    }
    setUploading(true);
    setError("");
    try {
      const prepared = await prepareAttachmentFile(file, {
        maxEdge: 1280,
        quality: 0.8,
      });
      const formData = new FormData();
      formData.append("file", prepared);
      if (remarks.trim()) formData.append("remarks", remarks.trim());
      const res = await axiosInstance.post(basePath, formData);
      if (fileInputRef.current) fileInputRef.current.value = "";
      setChosenFileName("");
      setRemarks("");
      await loadAttachments();
      const createdId = res.data?.data?.Tenant_Client_Attachment_Id;
      if (createdId) setSelectedId(createdId);
    } catch (err) {
      setError(err.response?.data?.message || "Could not upload file.");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = (attachment) => {
    setDeleteTarget(attachment);
  };

  const closeDeleteConfirm = () => {
    if (deletingId) return;
    setDeleteTarget(null);
  };

  const confirmDelete = async () => {
    const attachment = deleteTarget;
    if (!attachment) return;
    setDeletingId(attachment.Tenant_Client_Attachment_Id);
    setError("");
    try {
      await axiosInstance.delete(`${basePath}/${attachment.Tenant_Client_Attachment_Id}`);
      setDeleteTarget(null);
      await loadAttachments();
    } catch (err) {
      setError(err.response?.data?.message || "Could not delete attachment.");
    } finally {
      setDeletingId(null);
    }
  };

  if (!open) return null;

  return (
    <div className="cportal-modal-overlay" onClick={onClose} role="presentation">
      <div
        className="cportal-modal cportal-modal--wide cportal-attachments-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="entity-attachments-title"
      >
        <div className="cportal-modal-header">
          <h3 id="entity-attachments-title">
            Attachments
            {entityName ? (
              <span className="cportal-attachments-modal-sub"> — {entityName}</span>
            ) : null}
          </h3>
          <button type="button" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="cportal-attachments-shell">
          <aside className="cportal-attachments-sidebar" aria-label="Attachment files">
            <div className="cportal-attachments-sidebar-head">Files</div>
            {loading ? (
              <p className="cportal-muted cportal-attachments-sidebar-empty">Loading…</p>
            ) : attachments.length === 0 ? (
              <p className="cportal-muted cportal-attachments-sidebar-empty">No files yet</p>
            ) : (
              <ul className="cportal-attachments-name-list">
                {attachments.map((attachment) => {
                  const active = attachment.Tenant_Client_Attachment_Id === selectedId;
                  return (
                    <li key={attachment.Tenant_Client_Attachment_Id}>
                      <button
                        type="button"
                        className={`cportal-attachments-name-item${active ? " is-active" : ""}`}
                        onClick={() => setSelectedId(attachment.Tenant_Client_Attachment_Id)}
                        title={attachment.File_Name || "File"}
                      >
                        {isImageAttachment(attachment) ? null : (
                          <FaPaperclip aria-hidden="true" />
                        )}
                        <span>{attachment.File_Name || "Untitled"}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </aside>

          <section className="cportal-attachments-main">
            <form className="cportal-attachments-upload-bar" onSubmit={handleUpload}>
              <div className="cportal-file-pick">
                <input
                  ref={fileInputRef}
                  id="entity-attachment-file"
                  className="cportal-file-pick-input"
                  type="file"
                  disabled={uploading}
                  accept=".png,.jpg,.jpeg,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.md"
                  required
                  onChange={(e) => {
                    const name = e.target.files?.[0]?.name || "";
                    setChosenFileName(name);
                  }}
                />
                <label htmlFor="entity-attachment-file" className="cportal-file-pick-btn">
                  Choose file
                </label>
                <span className="cportal-file-pick-name" title={chosenFileName || undefined}>
                  {chosenFileName || "No file chosen"}
                </span>
              </div>
              <input
                type="text"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Remarks (optional)"
                disabled={uploading}
              />
              <button type="submit" className="cportal-btn cportal-btn-primary" disabled={uploading}>
                <FaUpload aria-hidden="true" />
                {uploading ? "Uploading…" : "Upload"}
              </button>
            </form>

            <div className="cportal-attachments-sep" role="separator" aria-hidden="true" />

            <PortalAlert type="error" message={error} onClear={() => setError("")} />

            <div
              className={`cportal-attachments-preview${
                selectedIsImage ? " cportal-attachments-preview--fitted" : ""
              }`}
            >
              {loading ? (
                <p className="cportal-muted">Loading attachments…</p>
              ) : !selected ? (
                <p className="cportal-muted">Select a file on the left, or upload one.</p>
              ) : selectedIsImage ? (
                <div className="cportal-attachments-zoom-stage">
                  <img
                    src={selectedUrl}
                    alt={selected.File_Name || "Attachment"}
                    className="cportal-attachments-preview-image"
                    style={{ transform: `scale(${zoom})` }}
                  />
                </div>
              ) : (
                <div className="cportal-attachments-preview-file">
                  <FaPaperclip aria-hidden="true" />
                  <strong>{selected.File_Name || "File"}</strong>
                  {selected.Remarks ? <span>{selected.Remarks}</span> : null}
                  {selectedUrl ? (
                    <a
                      className="cportal-btn cportal-btn-edit"
                      href={selectedUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Open file
                    </a>
                  ) : null}
                </div>
              )}
            </div>

            {selected ? (
              <div className="cportal-attachments-preview-footer">
                <div className="cportal-attachments-preview-meta">
                  <strong>{selected.File_Name || "—"}</strong>
                  {selected.Remarks ? <span>{selected.Remarks}</span> : null}
                </div>
                <div className="cportal-attachments-preview-actions">
                  {selectedIsImage ? (
                    <div className="cportal-attachments-zoom-controls" role="group" aria-label="Zoom">
                      <button
                        type="button"
                        className="cportal-btn cportal-btn-ghost cportal-btn-sm"
                        onClick={() => setZoom((z) => Math.max(0.5, Number((z - 0.25).toFixed(2))))}
                        disabled={zoom <= 0.5}
                        title="Zoom out"
                        aria-label="Zoom out"
                      >
                        <FaSearchMinus aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        className="cportal-attachments-zoom-reset"
                        onClick={() => setZoom(1)}
                        title="Reset zoom"
                      >
                        {Math.round(zoom * 100)}%
                      </button>
                      <button
                        type="button"
                        className="cportal-btn cportal-btn-ghost cportal-btn-sm"
                        onClick={() => setZoom((z) => Math.min(3, Number((z + 0.25).toFixed(2))))}
                        disabled={zoom >= 3}
                        title="Zoom in"
                        aria-label="Zoom in"
                      >
                        <FaSearchPlus aria-hidden="true" />
                      </button>
                    </div>
                  ) : null}
                  {selectedUrl ? (
                    <a
                      className="cportal-btn cportal-btn-edit"
                      href={selectedUrl}
                      download={selected.File_Name || "attachment"}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <FaDownload aria-hidden="true" />
                      Download
                    </a>
                  ) : null}
                  {canDelete ? (
                    <button
                      type="button"
                      className="cportal-btn cportal-btn-ghost"
                      onClick={() => handleDelete(selected)}
                      disabled={deletingId === selected.Tenant_Client_Attachment_Id}
                    >
                      <FaTrash aria-hidden="true" />
                      {deletingId === selected.Tenant_Client_Attachment_Id ? "Deleting…" : "Delete"}
                    </button>
                  ) : null}
                </div>
              </div>
            ) : null}
          </section>
        </div>
      </div>

      <PortalConfirmModal
        open={!!deleteTarget}
        title="Delete Attachment"
        message={
          deleteTarget ? (
            <p>
              Delete <strong>{deleteTarget.File_Name}</strong>? This cannot be undone.
            </p>
          ) : null
        }
        confirming={!!deletingId}
        confirmingLabel="Deleting…"
        onConfirm={confirmDelete}
        onClose={closeDeleteConfirm}
      />
    </div>
  );
}

export default PortalEntityAttachmentsModal;
